﻿/**
 * Date / time / date-time picker with an OK-Cancel popover.
 *
 * The native pickers of `<input type="date|time|datetime-local">` are browser UI living outside
 * the DOM: they cannot be styled, extended or given buttons, and there is no API to close one.
 * So the click that would open one is cancelled and this popover opens instead, holding the
 * calendar we already have plus, for the time part, a plain native time input.
 *
 * Picking inside the popover only moves a draft. The value reaches the caller when OK is pressed;
 * Cancel, Escape and a click outside all discard it — the last two for free, from the popover's
 * own light dismiss.
 */

import {DateOnly, DateTime, isNullOrEmpty} from "@2d-front-monorepo/core";
import React, {ChangeEvent, JSX, useCallback, useMemo, useRef, useState} from "react";
import Button from "../Buttons/Button";
import Calendar from "../Calendar/Calendar";
import {getBoxModel} from "../helpers/BoxModel";
import {HtmlRequiredPrefixedControlProps} from "./HtmlControlProps";
import LabeledControl from "./LabeledControl";
import {useLabeledControl} from "./LabeledControlData";

export type DatePickerMode = "date" | "time" | "datetime";

export interface DatePickerMessages {
	ok?: string;
	cancel?: string;
	/** Accessible name of the clock inside the popover. */
	time?: string;
	/**
	 * Accessible name of the popover itself. Deliberately not the field's own label: sharing it
	 * would make every `getByLabelText`/"find by name" on the field ambiguous, here and for callers.
	 */
	title?: string;
}

const DEFAULT_MESSAGES: Required<DatePickerMessages> = {
	ok: "OK",
	cancel: "Cancel",
	time: "Time",
	title: "Choose a value"
};

const INPUT_TYPES: Record<DatePickerMode, string> = {
	date: "date",
	time: "time",
	datetime: "datetime-local"
};

/** The two halves of a value is edited as, each in the shape its own input speaks. */
export interface DatePickerParts {
	/** `yyyy-MM-dd`, or empty. */
	date: string;
	/** `HH:mm` or `HH:mm:ss`, or empty. */
	time: string;
}

/** Splits a control value into the parts of the popover edits. Anything unparseable reads as empty. */
export function splitValue(value: string | undefined, mode: DatePickerMode): DatePickerParts {
	if (isNullOrEmpty(value)) {
		return {date: "", time: ""};
	}

	switch (mode) {
		case "date":
			return {date: value.split("T")[0], time: ""};
		case "time":
			return {date: "", time: value};
		case "datetime": {
			const [date, time] = value.split("T");
			return {date, time: time ?? ""};
		}
	}
}

/**
 * Fills in whatever half the value does not carry, from the clock on the wall. An empty picker
 * already shows today on the calendar; leaving the draft empty behind that would make OK mean
 * "nothing" on a popover that reads as "today", and a date-time or a time could not be set to
 * now at all without typing it. What the popover shows is what OK commits.
 */
export function seedNow(parts: DatePickerParts, mode: DatePickerMode, withSeconds: boolean): DatePickerParts {
	const hasDate = mode !== "time";
	const hasTime = mode !== "date";
	if ((!hasDate || !isNullOrEmpty(parts.date)) && (!hasTime || !isNullOrEmpty(parts.time))) {
		return parts;
	}

	// localNow keeps the local wall clock in the fields toJSON reads, so this is the user's own
	// date and time, not UTC's.
	const now = DateTime.localNow().toJSON();
	return {
		date: hasDate && isNullOrEmpty(parts.date) ? now.substring(0, 10) : parts.date,
		time: hasTime && isNullOrEmpty(parts.time) ? now.substring(11, withSeconds ? 19 : 16) : parts.time
	};
}

/**
 * Rebuilds a control value from the edited parts. A date-time with no date is no value at all,
 * while one with no time is midnight — the calendar is what the user came for, the clock is
 * something they may never touch.
 */
export function composeValue(parts: DatePickerParts, mode: DatePickerMode): string {
	switch (mode) {
		case "date":
			return parts.date;
		case "time":
			return parts.time;
		case "datetime":
			return isNullOrEmpty(parts.date) ? "" : `${parts.date}T${isNullOrEmpty(parts.time) ? "00:00" : parts.time}`;
	}
}

export interface DatePickerProps extends HtmlRequiredPrefixedControlProps<HTMLInputElement> {
	autoFocus?: boolean;
	/** What is being picked. Defaults to `date`. */
	mode?: DatePickerMode;
	max?: string;
	min?: string;
	/** Seconds granularity of the time part, passed straight to the time inputs. */
	step?: number | "any";
	/**
	 * Commits the day as soon as it is clicked, with no footer to confirm through. Only
	 * `date` mode honours it: there the single click IS the whole value, while a time or a
	 * date-time is picked in several moves and needs a point at which the user says "done".
	 */
	autoAccept?: boolean;
	messages?: DatePickerMessages;
	isSelectable?: (date: DateOnly) => boolean;
	dayClassName?: (date: DateOnly) => string | undefined;
}

export default function DatePicker(props: DatePickerProps): JSX.Element {
	const {
		label,
		name,
		value,
		id,
		column,
		disabled,
		hidden,
		onChange,
		onBlur,
		ref,
		autoFocus,

		required,
		hint,
		error,
		inlineError,

		controlPrefix,
		controlSuffix,

		mode = "date",
		min,
		max,
		step,
		autoAccept,
		messages,
		isSelectable,
		dayClassName

	} = props;

	const {componentId, ariaRef} = useLabeledControl(id, hint, error);
	const [draft, setDraft] = useState<DatePickerParts>(() => splitValue(value, mode));

	const [open, setOpen] = useState(false);

	const spanRef = useRef<HTMLSpanElement>(null);
	const calendarRef = useRef<HTMLDivElement>(null);

	const msg = useMemo(() => ({...DEFAULT_MESSAGES, ...messages}), [messages]);
	const hasDate = mode !== "time";
	const hasTime = mode !== "date";
	// A step finer than a minute is what makes the time inputs carry seconds, so it is also what
	// decides how precise a seeded "now" has to be.
	const withSeconds = step === "any" || (typeof step === "number" && step < 60);
	const picksInOneClick = autoAccept === true && mode === "date";

	const handleClick = useCallback((e: React.MouseEvent<HTMLInputElement>) => {
		e.preventDefault();
		if (!calendarRef.current || !spanRef.current || disabled) {
			return;
		}

		const calendar = calendarRef.current;
		calendar.togglePopover(true);
		const inputBox = getBoxModel(spanRef.current).paddingBox;
		const calendarBox = getBoxModel(calendarRef.current).marginBox;

		// Centred under the field, then pulled back inside the viewport: the popover is wider than
		// the calendar alone now, so a field near either edge would otherwise hang off it.
		const centred = inputBox.left + inputBox.width / 2 - calendarBox.width / 2;
		const x = Math.max(4, Math.min(centred, window.innerWidth - calendarBox.width - 4));
		let y = inputBox.top + inputBox.height + 4;

		if (y + calendarBox.height > window.innerHeight) {
			y = inputBox.top - calendarBox.height - 4;
		}

		calendar.style.left = x + "px";
		calendar.style.top = y + "px";
	}, [disabled]);

	// The draft only ever starts from what the caller holds, and only when the popover opens:
	// a value arriving mid-edit (an external resync) must not overwrite what is being picked.
	const onToggle = useCallback(function (e: React.ToggleEvent<HTMLDivElement>): void {
		const isOpen = e.newState === "open";
		setOpen(isOpen);
		if (isOpen) {
			setDraft(seedNow(splitValue(value, mode), mode, withSeconds));
		}
	}, [value, mode, withSeconds]);

	// The popover does not take focus, so the field's own segments stay editable underneath it.
	// Typing there is an edit like any other, and the draft has to follow, or OK would commit the
	// stale draft straight over what was just typed.
	const fieldChange = useCallback(function (e: ChangeEvent<HTMLInputElement>): void {
		setDraft(splitValue(e.currentTarget.value, mode));
		onChange?.(e);
	}, [mode, onChange]);

	// The value leaves through the field itself, as the change event a caller already expects
	// from an <input>. React owns that input's value, so the write only has to survive until
	// the handler has read it.
	const emit = useCallback(function (next: string): void {
		calendarRef.current?.togglePopover(false);

		const input = spanRef.current?.querySelector("input");
		if (input == null || !onChange) {
			return;
		}

		input.value = next;

		const syntheticEvent = new Event("change", {bubbles: true});
		Object.defineProperty(syntheticEvent, "target", {value: input});
		Object.defineProperty(syntheticEvent, "currentTarget", {value: input});
		onChange(syntheticEvent as unknown as React.ChangeEvent<HTMLInputElement>);
	}, [onChange]);

	const calendarSelect = useCallback(function (date: DateOnly): void {
		if (picksInOneClick) {
			emit(date.toJSON());
			return;
		}

		setDraft(prev => ({...prev, date: date.toJSON()}));
	}, [picksInOneClick, emit]);

	const timeSelect = useCallback(function (e: React.ChangeEvent<HTMLInputElement>): void {
		const time = e.currentTarget.value;
		setDraft(prev => ({...prev, time}));
	}, []);

	const cancel = useCallback(function (): void {
		calendarRef.current?.togglePopover(false);
	}, []);

	const accept = useCallback(function (): void {
		emit(composeValue(draft, mode));
	}, [draft, mode, emit]);

	// Only a calendar has day bounds. In time mode min/max are clock strings ("08:00:00"), which
	// DateOnly cannot parse — it would throw here, mid-render, for a value nothing even reads.
	const [minValue, maxValue] = useMemo(() => {
		if (!hasDate) {
			return [undefined, undefined];
		}

		const minValue = isNullOrEmpty(min) ? undefined : DateOnly.fromIsoString(min);
		const maxValue = isNullOrEmpty(max) ? undefined : DateOnly.fromIsoString(max);
		return [minValue, maxValue];
	}, [min, max, hasDate]);

	// Rebuilt only when the day string changes: Calendar re-reads its month off this instance,
	// so a fresh object every render would put it in a render loop.
	const selectedDate = useMemo(
		() => isNullOrEmpty(draft.date) ? DateOnly.today() : DateOnly.fromIsoString(draft.date),
		[draft.date]);

	if (hidden) {
		return <></>;
	}

	return <>
		<LabeledControl label={label}
		                type={INPUT_TYPES[mode]}
		                id={componentId}
		                required={required}
		                disabled={disabled}
		                column={column}
		                error={error}
		                inlineError={inlineError}
		                hint={hint}
		                hidden={hidden}
		                ariaRef={ariaRef}
		                controlPrefix={controlPrefix}
		                controlSuffix={controlSuffix}
		                ref={spanRef}>
			<input
				id={componentId}
				type={INPUT_TYPES[mode]}
				ref={ref}
				onChange={fieldChange}
				onBlur={onBlur}
				onClick={handleClick}
				name={name}
				value={value}
				autoFocus={autoFocus}
				max={max}
				min={min}
				step={step}
				readOnly={disabled}
				required={required}
				aria-describedby={ariaRef.current?.describedBy}
				aria-invalid={ariaRef.current?.invalid}
				aria-required={required}
				aria-haspopup={disabled ? undefined : "dialog"}
				aria-expanded={disabled ? undefined : open}
				tabIndex={disabled ? -1 : undefined}
			/>
		</LabeledControl>
		{!disabled && <div popover="auto" className="popover-calendar" ref={calendarRef} onToggle={onToggle}
		                   role="dialog" aria-label={msg.title}>
			{hasDate && <Calendar selected={selectedDate} onSelect={calendarSelect} isSelectable={isSelectable}
                                  dayClassName={dayClassName} minValue={minValue} maxValue={maxValue}/>}
			{hasTime && <div className="popover-calendar__time">
                {/* ponytail: the native time input, same call TimeSelector already made — its
                    hand-rolled clock never got built. Swap this one element if one ever is.
                    Bounds go on it only where the value IS a time: in datetime mode min/max are
                    whole instants, whose clock part only applies on the edge day. */}
                <input type="time" value={draft.time} step={step} onChange={timeSelect}
                       aria-label={msg.time}
                       min={mode === "time" ? min : undefined}
                       max={mode === "time" ? max : undefined}/>
            </div>}
			{!picksInOneClick && <div className="popover-calendar__footer">
				<Button small={true} onClick={cancel}>{msg.cancel}</Button>
				{/* NOT kind="primary": that renders type="submit", which a host form would act on. */}
				<Button small={true} kind={"confirm"} onClick={accept}>{msg.ok}</Button>
			</div>}
        </div>}
	</>
}
